import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { AXIS } from './chartTheme';
import { AreaGradient } from './ChartKit';

/**
 * Seccion Vehiculos rehecha: composicion del parque (marcas y antiguedad),
 * curva de atendidos por mes, top modelos, y "parque dormido" accionable.
 */

function DeltaChip({ value }: { value: number | null }) {
  if (value == null) return <span className="text-xs text-muted-foreground">sin ref.</span>;
  const color = value > 0 ? 'text-green-600 bg-green-500/10' : value < 0 ? 'text-red-600 bg-red-500/10' : 'text-muted-foreground bg-muted';
  return <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>{value > 0 ? '▲ +' : value < 0 ? '▼ ' : ''}{value}% vs per. ant.</span>;
}

const waLink = (phone: string, cliente: string, vehiculo: string) => {
  const p = String(phone || '').replace(/\D/g, '');
  if (!p) return null;
  const full = p.startsWith('54') ? p : '54' + p;
  const msg = encodeURIComponent(`Hola ${cliente.split(' ')[0]}! Te escribimos de ROCAM. Hace un tiempo que no vemos el ${vehiculo} por el taller, ¿todo en orden con las cubiertas?`);
  return `https://wa.me/${full}?text=${msg}`;
};

const TRAMO_COLORS = ['#1baf7a', '#378ADD', '#EF9F27', '#D85A30'];

export default function VehiculosSection({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setError('');
    setLoading(true);
    axios.get('/api/reports/tablero/vehiculos', { params: { startDate, endDate } })
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando vehiculos'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando vehículos...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  const totalAntiguedad = d.antiguedad.reduce((a: number, t: any) => a + t.n, 0) || 1;

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Parque registrado</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.parque.value}</p>
          <span className="text-xs text-muted-foreground">vehículos en el sistema</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Atendidos en el período</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.atendidos.value}</p>
          <DeltaChip value={d.kpis.atendidos.deltaPct} />
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Antigüedad promedio</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.antiguedadProm.value != null ? `${d.kpis.antiguedadProm.value} años` : '—'}</p>
          <span className="text-xs text-muted-foreground">sobre {d.kpis.antiguedadProm.base} con año cargado</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Con km cargado</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.conKm.value}</p>
          <span className="text-xs text-muted-foreground">cargá km en las OT para seguimiento de service</span>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1">Vehículos atendidos por mes</h3>
            <p className="text-xs text-muted-foreground mb-3">Vehículos únicos con remito u OT (desde que arrancó el sistema)</p>
            <ChartContainer config={{ atendidos: { label: 'Atendidos', color: 'hsl(var(--chart-2))' } }} className="h-[220px] w-full">
              <AreaChart data={d.atendidosSerie} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <AreaGradient id="fillAtendidos" color="var(--color-atendidos)" />
                </defs>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                <XAxis dataKey="label" {...AXIS} />
                <YAxis {...AXIS} width={30} allowDecimals={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area type="monotone" dataKey="atendidos" stroke="var(--color-atendidos)" strokeWidth={2.4} fill="url(#fillAtendidos)" dot={{ r: 3 }} />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1">Antigüedad del parque</h3>
            <p className="text-xs text-muted-foreground mb-1">Autos más viejos = más reparación, menos 0km</p>
            <ChartContainer config={{ n: { label: 'Vehículos' } }} className="h-[200px] w-full">
              <PieChart>
                <Pie data={d.antiguedad} dataKey="n" nameKey="tramo" cx="50%" cy="50%" innerRadius={48} outerRadius={78} paddingAngle={2}>
                  {d.antiguedad.map((_: any, i: number) => <Cell key={i} fill={TRAMO_COLORS[i % TRAMO_COLORS.length]} />)}
                </Pie>
                <ChartTooltip content={<ChartTooltipContent nameKey="tramo" />} />
              </PieChart>
            </ChartContainer>
            <div className="flex flex-wrap gap-3 justify-center text-xs text-muted-foreground">
              {d.antiguedad.map((t: any, i: number) => (
                <span key={t.tramo}>
                  <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 align-middle" style={{ background: TRAMO_COLORS[i] }} />
                  {t.tramo}: {t.n} ({Math.round((t.n / totalAntiguedad) * 100)}%)
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1">Marcas del parque</h3>
            <p className="text-xs text-muted-foreground mb-3">Guía para stock de cubiertas y campañas</p>
            <ChartContainer config={{ n: { label: 'Vehículos', color: 'hsl(var(--chart-3))' } }} className="w-full" style={{ height: Math.max(180, d.marcas.length * 30) }}>
              <BarChart data={d.marcas} layout="vertical" margin={{ top: 0, right: 30, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} horizontal={false} />
                <XAxis type="number" {...AXIS} allowDecimals={false} />
                <YAxis type="category" dataKey="marca" {...AXIS} width={96} />
                <ChartTooltip content={<ChartTooltipContent formatter={(v: any, _n: any, item: any) => (
                  <>
                    <span className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ background: item?.color || item?.payload?.fill }} />
                    <div className="flex flex-1 items-center justify-between gap-3 leading-none">
                      <span className="text-muted-foreground">Parque</span>
                      <span className="font-mono font-medium tabular-nums text-foreground">{v} veh. ({item?.payload?.pct}%)</span>
                    </div>
                  </>
                )} />} />
                <Bar dataKey="n" fill="var(--color-n)" radius={[0, 3, 3, 0]} maxBarSize={18} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1">Top modelos atendidos</h3>
            <p className="text-xs text-muted-foreground mb-3">En el período seleccionado</p>
            {d.topModelos.length === 0 && <p className="text-sm text-muted-foreground">Sin visitas con vehículo en el período</p>}
            {d.topModelos.length > 0 && (
              <ChartContainer config={{ visitas: { label: 'Visitas', color: 'hsl(var(--chart-2))' } }} className="w-full" style={{ height: Math.max(160, d.topModelos.length * 32) }}>
                <BarChart data={d.topModelos} layout="vertical" margin={{ top: 0, right: 30, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} horizontal={false} />
                  <XAxis type="number" {...AXIS} allowDecimals={false} />
                  <YAxis type="category" dataKey="modelo" {...AXIS} width={130} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="visitas" fill="var(--color-visitas)" radius={[0, 3, 3, 0]} maxBarSize={18} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="flex items-baseline justify-between mb-1">
            <h3 className="font-semibold text-sm">Parque dormido</h3>
            <span className="text-xs text-muted-foreground">vehículos con historial, 90+ días sin venir</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Cada uno de estos autos sigue rodando — y va a cambiar cubiertas en algún lado.</p>
          {d.dormidos.length === 0 && <p className="text-sm text-muted-foreground">Ningún vehículo con historial lleva más de 90 días sin venir. 🎉</p>}
          {d.dormidos.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground text-left">
                    <th className="pb-1.5 font-normal">Patente</th>
                    <th className="pb-1.5 font-normal">Vehículo</th>
                    <th className="pb-1.5 font-normal">Cliente</th>
                    <th className="pb-1.5 font-normal text-right">Visitas</th>
                    <th className="pb-1.5 font-normal text-right">Sin venir hace</th>
                    <th className="pb-1.5 font-normal text-right">Contacto</th>
                  </tr>
                </thead>
                <tbody>
                  {d.dormidos.map((v: any) => {
                    const wa = v.phone ? waLink(v.phone, v.cliente, v.vehiculo) : null;
                    return (
                      <tr key={v.id} className="border-t">
                        <td className="py-1.5 pr-2 font-mono text-xs">{v.plate}</td>
                        <td className="py-1.5 pr-2 truncate max-w-[160px]">{v.vehiculo}</td>
                        <td className="py-1.5 pr-2 truncate max-w-[180px]">{v.cliente}</td>
                        <td className="py-1.5 pr-2 text-right text-muted-foreground">{v.visitas}</td>
                        <td className={`py-1.5 pr-2 text-right ${v.dias > 180 ? 'text-red-600' : 'text-amber-600'}`}>{v.dias} días</td>
                        <td className="py-1.5 text-right">
                          {wa ? <a href={wa} target="_blank" rel="noreferrer" className="text-green-600 hover:underline text-xs">WhatsApp →</a> : <span className="text-xs text-muted-foreground">sin teléfono</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
